import TrialLock from '../components/TrialLock';
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FaArrowRight, FaCheck, FaCheckCircle, FaCircle, FaChevronDown, FaChevronLeft, FaChevronRight, FaChevronUp, FaCreditCard, FaKey, FaRobot, FaServer, FaShieldAlt, FaTerminal } from 'react-icons/fa';
import { RootState } from '../store/store';
import axiosInstance from '../utility/axiosInstance';
import { formatConsoleDateTime } from '../utility/formatConsoleDate';
import '../styles/HomePage.scss';

type McpConnection = {
    client_name: string;
    client_version: string | null;
    first_connected_at: string;
    last_connected_at: string;
    legacy?: boolean;
};

type OnboardingStatus = {
    mcp_connected: boolean;
    mcp_connected_at: string | null;
    mcp_connections: McpConnection[];
    card_connected: boolean;
    budget_set: boolean;
    // A card account must have a budget (BUDGET_MANDATORY_PLAN_2026-10-28 section 5.6): the step says Required.
    budget_required?: boolean;
};

const displayMcpClient = (clientName: string) => {
    const name = clientName.trim().toLowerCase();
    if (name === 'codex' || name === 'codex-cli' || name === 'codex cli') return 'Codex';
    if (name === 'claude-code' || name === 'claude code') return 'Claude Code';
    return clientName;
};

const HomePage: React.FC = () => {
    const [isChecklistCollapsed, setChecklistCollapsed] = useState(false);
    const [mcpStatus, setMcpStatus] = useState<OnboardingStatus | null>(null);
    const [mcpStatusFailed, setMcpStatusFailed] = useState(false);
    const [isMcpConnectionsExpanded, setMcpConnectionsExpanded] = useState(false);
    const keypairs = useSelector((state: RootState) => state.keypairs.data);
    const instances = useSelector((state: RootState) => state.instances.data);

    useEffect(() => {
        let mounted = true;
        axiosInstance.get<OnboardingStatus>('/v1/onboarding')
            .then(({ data }) => {
                if (!mounted) return;
                setMcpStatus({ ...data, mcp_connections: data.mcp_connections ?? [] });
                setMcpStatusFailed(false);
            })
            .catch(() => { if (mounted) setMcpStatusFailed(true); });
        return () => { mounted = false; };
    }, []);

    const setupItems = useMemo(() => [
        {
            id: 'mcp',
            label: 'Connect redu MCP',
            detail: mcpStatusFailed ? 'Could not verify connection' : mcpStatus === null ? 'Checking connection…' : '',
            complete: mcpStatus?.mcp_connected === true,
            to: '/mcp',
            icon: FaRobot,
        },
        { id: 'ssh', label: 'Add an SSH key', detail: '', complete: keypairs.length > 0, to: '/category/compute/ssh-keypairs', icon: FaKey },
        {
            id: 'billing',
            label: 'Connect a card',
            detail: mcpStatusFailed ? 'Could not verify payment method' : mcpStatus === null ? 'Checking payment method…' : '',
            complete: mcpStatus?.card_connected === true,
            to: '/category/user/billing?setup-billing=1',
            icon: FaCreditCard,
        },
        {
            id: 'budget',
            label: 'Set a monthly budget',
            detail: mcpStatusFailed ? 'Could not verify budget' : mcpStatus === null ? 'Checking budget…' : mcpStatus.budget_required === true ? 'Required' : 'Recommended',
            complete: mcpStatus?.budget_set === true,
            to: '/category/user/billing?setup-budget=1',
            icon: FaShieldAlt,
        },
        { id: 'deploy', label: 'Deploy your first resource', detail: '', complete: instances.length > 0, to: '/deploy', icon: FaServer },
    ], [instances.length, keypairs.length, mcpStatus, mcpStatusFailed]);
    const completeCount = setupItems.filter((item) => item.complete).length;
    const mcpConnections = mcpStatus?.mcp_connections ?? [];

    useEffect(() => {
        if (completeCount === setupItems.length) setChecklistCollapsed(true);
    }, [completeCount, setupItems.length]);

    return <div className="home-page-wrapper">
        <main className={`console-home console-home--showcase${isChecklistCollapsed ? ' console-home--checklist-collapsed' : ''}`}>
            <div className="console-home__main">
                <section className="home-ai-showcase" aria-labelledby="home-showcase-title">
                    <div className="home-ai-showcase__copy">
                        <span className="home-kicker">redu MCP · Model Context Protocol</span>
                        <h1 id="home-showcase-title">Infrastructure your agents run.</h1>
                        <div className="home-ai-showcase__actions">
                            <Link to="/mcp" className="home-primary-action">Set up redu MCP <FaArrowRight /></Link>
                            <TrialLock locked={false} paidResource><Link to="/deploy" className="home-secondary-action">Deploy infrastructure</Link></TrialLock>
                        </div>
                    </div>

                    <div className="home-agent-demo" aria-label="Example agent deployment plan">
                        <div className="home-agent-demo__topline"><span><FaTerminal /> Claude Code</span><span>Live plan</span></div>
                        <div className="home-agent-message home-agent-message--user"><span>You</span><p>Deploy this project to redu.</p></div>
                        <div className="home-agent-plan">
                            <span>Plan</span>
                            <p><FaCheck /> App VM · EU region</p>
                            <p><FaCheck /> Managed Postgres · private network</p>
                            <p><FaCheck /> Public URL · health-checked</p>
                        </div>
                    </div>
                </section>
                <div className="home-showcase-footer"><TrialLock locked={false} paidResource><Link to="/deploy">Open the deployment launcher <FaArrowRight /></Link></TrialLock></div>
            </div>

            <aside className="home-setup-checklist" aria-label="Account setup checklist">
                <div className="home-setup-checklist__header">
                    {!isChecklistCollapsed && <div><span>Getting started</span><strong>{completeCount} of {setupItems.length} complete</strong></div>}
                    <button type="button" onClick={() => setChecklistCollapsed((collapsed) => !collapsed)} aria-label={isChecklistCollapsed ? 'Expand setup checklist' : 'Minimize setup checklist'} title={isChecklistCollapsed ? 'Expand checklist' : 'Minimize checklist'}>
                        {isChecklistCollapsed ? <FaChevronLeft /> : <FaChevronRight />}
                    </button>
                </div>
                {!isChecklistCollapsed && <>
                    <div className="home-setup-checklist__progress"><span style={{ width: `${(completeCount / setupItems.length) * 100}%` }} /></div>
                    <div className="home-setup-checklist__items">
                        {setupItems.map((item) => {
                            const Icon = item.icon;
                            if (item.id === 'mcp' && item.complete) {
                                return <div key={item.id} className="home-setup-item-group is-complete">
                                    <button
                                        type="button"
                                        className="home-setup-item home-setup-item--accordion"
                                        onClick={() => setMcpConnectionsExpanded((expanded) => !expanded)}
                                        aria-expanded={isMcpConnectionsExpanded}
                                        aria-controls="mcp-connection-list"
                                    >
                                        <span className="home-setup-item__status"><FaCheckCircle /></span>
                                        <Icon className="home-setup-item__icon" />
                                        <span><strong>{item.label}</strong><small>{mcpConnections.length === 1 ? '1 connected client' : `${mcpConnections.length} connected clients`}</small></span>
                                        <span className="home-setup-item__disclosure" aria-hidden="true">{isMcpConnectionsExpanded ? <FaChevronUp /> : <FaChevronDown />}</span>
                                    </button>
                                    {isMcpConnectionsExpanded && <section id="mcp-connection-list" className="home-mcp-connections" aria-label="Connected MCP clients">
                                        <h2>Connected clients</h2>
                                        <ul>
                                            {mcpConnections.map((connection) => <li key={connection.client_name}>
                                                <span><strong>{displayMcpClient(connection.client_name)}</strong>{connection.client_version && <small>Version {connection.client_version}</small>}</span>
                                                <time dateTime={connection.last_connected_at}>Last connected {formatConsoleDateTime(connection.last_connected_at)}</time>
                                            </li>)}
                                        </ul>
                                        <Link to="/mcp">Connect another client <FaArrowRight /></Link>
                                    </section>}
                                </div>;
                            }
                            const link = <Link key={item.label} to={item.to} className={`home-setup-item${item.complete ? ' is-complete' : ''}`}>
                                <span className="home-setup-item__status">{item.complete ? <FaCheckCircle /> : <FaCircle />}</span>
                                <Icon className="home-setup-item__icon" />
                                <span><strong>{item.label}</strong>{item.detail && <small>{item.detail}</small>}</span>
                            </Link>;
                            // Only the deploy step creates paid resources; "Connect a card" and the rest stay links in any billing state.
                            return item.id === 'deploy' ? <TrialLock key={item.label} locked={false} paidResource>{link}</TrialLock> : link;
                        })}
                    </div>
                </>}
            </aside>
        </main>
    </div>;
};

export default HomePage;
